import "./style.scss";

import { InputWrapper } from "@amzn/stencil-react-components/input";
import { Container } from "@amzn/stencil-react-components/layout";
import { ScreenReaderOnly } from "@amzn/stencil-react-components/screen-reader-only";
import { SearchField } from "@amzn/uxm-stencil-react-components/lib/src/stencil/search-field";
import {
  buildTopNResults,
  calculateBucket,
  EventTrigger,
  generateQueryId,
  ONE_INDEX_BASED_RANK,
  SearchBoxResultsShownPayload,
  trackSearchBoxResultClicked,
  trackSearchBoxResultsError,
  trackSearchBoxResultsShown,
} from "common/helpers/search-analytics";
import { TrafficFrom } from "common/helpers/traffic-from";
import { CatalogItemDomains, CatalogURLQueryStrings } from "common/types";
import { CatalogicalItem } from "common/types/catalogical-type";
import { getString } from "common/uistringlabels/uiStringUtils";
import { PERMISSIONS } from "config";
import { debounce } from "lib/helpers/debounce";
import { UserPermissions } from "lib/services";
import { CatalogicalSearchRequestContent, SearchExperienceService } from "lib/services/shop-search-service";
import { ItemTaxonomyId } from "lib/taxonomy/item-taxonomy-id";
import React, { FunctionComponent, MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { AutocompleteSearchResult } from "../AutocompleteSearchResult";

const fieldsToExclude = [
  "description",
  "licenseActivationInstructions",
  "rulesApprovalsTemplate",
  "embeddings",
  "specifications",
  "resellerInformation",
  "manufacturer",
  "catalogicalGenerated",
  "references",
  "autoGenProductDescription",
  "autoGenFormattedDescription",
];

export interface SearchExperienceSearchBoxProps {
  /**
   * The width of the Search Box
   */
  width?: string;
  /**
   * The query string
   */
  queryText?: string | null;

  /**
   * The touchpoint value to be send to UXM
   */
  touchpointId: string;
}

export interface SearchBoxItem {
  name: string;
  taxonomyId: string;
}
const SOFTWARE_DOMAIN = CatalogItemDomains.SOFTWARE;

export const SearchExperienceSearchBox: FunctionComponent<SearchExperienceSearchBoxProps> = ({
  width,
  queryText = "",
  touchpointId,
}) => {
  let placeholderText = getString("searchBox.placeholder");
  /* istanbul ignore next */
  if (location.pathname == "/") {
    placeholderText = getString("searchBox.placeholder_home");
  }

  const toggleUseAiIntent = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_USE_INTENT);

  const [query, setQuery] = useState<string>(queryText ?? "");
  const [totalItemsCount, setTotalItemsCount] = useState<number | undefined>(undefined);
  const [results, setResults] = useState<CatalogicalItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showDropdown, setShowDropdown] = useState<boolean>(true);
  const currentQueryRef = useRef<string>(queryText || "");
  const urlParams = new URLSearchParams(location.search);

  const queryId: MutableRefObject<string | null> = useRef(null);
  const searchEventTrigger = useRef<EventTrigger>(EventTrigger.Unknown);

  useEffect(() => {
    setQuery(urlParams.get(CatalogURLQueryStrings.QUERY) ?? "");
  }, []);

  const searchService = new SearchExperienceService(SOFTWARE_DOMAIN);

  const getSearchResultsFromAPI = async (searchText: string): Promise<CatalogicalItem[]> => {
    let result: CatalogicalItem[] = [];
    if (!(searchText?.length > 2)) {
      return [];
    }
    try {
      const request: CatalogicalSearchRequestContent = {
        limit: 5,
        useSemanticSearch: true,
        useIntent: toggleUseAiIntent,
        itemType: "software",
        searchQuery: searchText.toLowerCase(),
        excludeFields: fieldsToExclude.join(","),
      };
      const newQueryId = generateQueryId();
      queryId.current = newQueryId;
      const results = await searchService.getItems<CatalogicalItem>(request);

      // Use ref for more reliable race condition detection
      // Only update state if the current query still matches the search text
      if (currentQueryRef.current === searchText) {
        const totalCount = results.pagination.total;
        result = results.data.slice(0, 5);

        trackSearchBoxResultsShown({
          queryId: queryId.current,
          request: request,
          resultCount: totalCount ?? -1,
          visibleResultsCount: result.length,
          topNResults: buildTopNResults(result, 0, SOFTWARE_DOMAIN),
        });

        setTotalItemsCount(totalCount);
        setResults(result);
      }
    } catch (error) {
      trackSearchBoxResultsError({
        queryId: queryId.current ?? "-",
        error: (error as Error).message,
      });

      result = [];
      if (currentQueryRef.current === searchText) {
        setResults(result);
      }
    }
    setIsLoading(false);
    return result;
  };

  const getSearchResultsDebounced = useCallback(debounce(getSearchResultsFromAPI, 1000)[0], []);

  // Custom onChange handler that updates both state and ref
  const handleQueryChange = (newQuery: string) => {
    searchEventTrigger.current = EventTrigger.UserQuery;
    setQuery(newQuery);
    currentQueryRef.current = newQuery;
  };

  useEffect(() => {
    // Only perform search it was triggered by user input
    if (query && searchEventTrigger.current !== EventTrigger.Unknown) {
      setIsLoading(true);
      setShowDropdown(true);
      getSearchResultsDebounced(query);
    } else if (!query) {
      setResults([]);
      setTotalItemsCount(undefined);
      setIsLoading(false);
      setShowDropdown(true);
    }
  }, [query]);

  const resultCountTextHandler = () => {
    if (!showDropdown) {
      return "";
    }
    return totalItemsCount == null ? getString("searchBox.noResults") : `${totalItemsCount} results`;
  };

  const history = useHistory();

  // How to display the result once its selected
  const resultToStringHandler = (result: SearchBoxItem) => result.name;

  // When an item is selected from the autocomplete
  const onResultClickHandler = (option: SearchBoxItem): void => {
    let rank = results.findIndex((item) => item.taxonomyId === option.taxonomyId);
    rank = rank >= 0 ? rank + ONE_INDEX_BASED_RANK : rank;

    trackSearchBoxResultClicked({
      queryId: queryId.current,
      taxonomyId: option.taxonomyId,
      rank: rank,
      visibleResultsCount: results.length,
      totalResultsCount: totalItemsCount,
      bucket: calculateBucket(option, SOFTWARE_DOMAIN),
    });

    const itemTaxId = ItemTaxonomyId.Parse(option.taxonomyId);
    history.push(`/items/${itemTaxId.getUrlSuffix()}?from=${TrafficFrom.Autocomplete}`, {
      from: TrafficFrom.Autocomplete,
      queryId: queryId.current,
    });
  };

  // When the view all results is selected
  const onListActionButtonClickHandler = () => {
    history.push(`/search?query=${query}`, {
      from: TrafficFrom.Autocomplete,
      queryId: queryId.current,
    });
  };

  // textInput must be declared here so the ref can refer to it
  const textInput = useRef<HTMLInputElement>(null);

  const onSubmitHandler = (e: any) => {
    e.preventDefault();
    const searchQuery = textInput.current?.value;
    history.push(`/search?query=${searchQuery}`, {
      from: TrafficFrom.Autocomplete,
      queryId: queryId.current,
    });

    // Simply clear everything and hide dropdown permanently
    setResults([]);
    setTotalItemsCount(undefined);
    setIsLoading(false);
    setShowDropdown(false);

    // Blur the input to ensure dropdown closes
    textInput.current?.blur();
    return false;
  };

  const renderResultAutoComplete = (result: CatalogicalItem) => {
    return (
      <AutocompleteSearchResult
        logoUrl={result.logoUrl}
        categories={result.categories}
        taxonomyId={result.taxonomyId}
        name={result.name}
      />
    );
  };

  /**
   * Handles keyboard events in the search form.
   * When the Enter key is pressed with an empty search query,
   * redirects to the search page showing all software items
   * sorted alphabetically by name in ascending order.
   */
  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === "Enter" && !query?.trim()) {
      e.preventDefault();
      history.push(`/search?sort=name&sortDirection=asc`, {
        from: TrafficFrom.Autocomplete,
        queryId: queryId.current,
      });
    }
  };

  const getSearchTelemetry = (): Record<string, unknown> => {
    if (!queryId.current) {
      return {};
    }

    const payload: SearchBoxResultsShownPayload = {
      queryId: queryId.current,
      resultCount: totalItemsCount ?? -1,
      visibleResultsCount: results.length,
      topNResults: buildTopNResults(results, 0, SOFTWARE_DOMAIN),
    };

    return payload;
  };

  return (
    <Container isFullWidth={true}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <form role="search" onKeyDown={handleOnKeyDown} onSubmit={onSubmitHandler} target="/search">
        <InputWrapper
          id="search-box"
          labelText="Search"
          renderLabel={({ htmlFor, children }) => (
            <ScreenReaderOnly>
              <label htmlFor={htmlFor}>{children}</label>
            </ScreenReaderOnly>
          )}
        >
          {(inputProps: any) => (
            <SearchField
              ref={textInput}
              width={width}
              data-cy="search-input"
              placeholder={placeholderText}
              shouldAutocomplete={false}
              hasSubmitButton={true}
              {...inputProps}
              onChange={handleQueryChange}
              query={query}
              resultToString={showDropdown ? resultToStringHandler : undefined}
              renderResult={showDropdown ? renderResultAutoComplete : undefined}
              resultCountText={showDropdown ? resultCountTextHandler : undefined}
              onResultClick={showDropdown ? onResultClickHandler : undefined}
              onListActionButtonClick={showDropdown ? onListActionButtonClickHandler : undefined}
              isLoadingText={showDropdown ? "Searching..." : undefined}
              isLoading={showDropdown ? isLoading : false}
              results={showDropdown ? results : []}
              shouldShowListOnNoResults={showDropdown}
              touchpoint={touchpointId}
              customData={getSearchTelemetry()}
            />
          )}
        </InputWrapper>
      </form>
    </Container>
  );
};
